import { useAppDispatch } from '@/app/hooks';
import EtablissementAutocomplete from '@/components/Etablissement/EtablissementAutocomplete';
import SusAddressFields from '@/components/Sus/form/SusAddressFields';
import SusContactsFields from '@/components/Sus/form/SusContactsFields';
import SusDpiFields from '@/components/Sus/form/SusDpiFields';
import {
  emptyContact,
  susFormSchema,
  type SusFormValues,
} from '@/components/Sus/form/susFormSchema';
import SusFormSection from '@/components/Sus/form/SusFormSection';
import SusPositionField from '@/components/Sus/form/SusPositionField';
import SusSettingsFields from '@/components/Sus/form/SusSettingsFields';
import SusThresholdFields from '@/components/Sus/form/SusThresholdFields';
import { useGetSusByIdQuery, useSaveSusMutation } from '@/features/api/endpoints/suEndpoints';
import { closeDrawer } from '@/features/drawer/drawerSlice';
import SusFormDrawerLoader from '@/features/drawer/drawers/SusFormDrawerLoader';
import { susNumeroOrdreCodes, type SUSInput } from '@/models/sus.type';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Divider,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useTranslate } from '@tolgee/react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

type Props = {
  payload?: { susId: number };
};

const SEUIL_BAS_QUOTIDIEN_DEFAUT = 10;
const SEUIL_HAUT_QUOTIDIEN_DEFAUT = 10000;
const SEUIL_BAS_DUREE_PASSAGE_DEFAUT = 10;
const SEUIL_HAUT_DUREE_PASSAGE_DEFAUT = 100000;

/** Texte vide → `null` pour le flow. */
const emptyToNull = (value: string | null | undefined): string | null => value?.trim() || null;

/** `timetz` Directus (« 08:30:00+00 ») → valeur du champ heure (« 08:30 »). */
/** Directus renvoie les colonnes `numeric` (latitude, longitude…) sous forme de chaîne. */
const toNumberOrNull = (value: number | string | null | undefined): number | null =>
  value == null || value === '' ? null : Number(value);

const toTimeInput = (value: string | null | undefined): string => value?.slice(0, 5) ?? '';

const toSaveArg = ({
  adr_num_voie,
  adr_type_voie,
  adr_nom_voie,
  adr_lieudit,
  adr_code_postal,
  adr_commune,
  heure_premier_envoi_quotidien,
  metadonnees,
  ...rest
}: SusFormValues): SUSInput => ({
  ...rest,
  metadonnees: {
    ...metadonnees,
    logiciel: emptyToNull(metadonnees.logiciel),
    dpi_version: emptyToNull(metadonnees.dpi_version),
    dpi_bascule_precisions: emptyToNull(metadonnees.dpi_bascule_precisions),
  },
  adr_num_voie: emptyToNull(adr_num_voie),
  adr_type_voie: emptyToNull(adr_type_voie),
  adr_nom_voie: emptyToNull(adr_nom_voie),
  adr_lieudit: emptyToNull(adr_lieudit),
  adr_code_postal: emptyToNull(adr_code_postal),
  adr_commune: emptyToNull(adr_commune),
  heure_premier_envoi_quotidien: `${heure_premier_envoi_quotidien}:00`,
});

const SusFormDrawer = ({ payload }: Props) => {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const [saveSus] = useSaveSusMutation();

  const isEdit = payload?.susId !== undefined;

  const {
    data: sus,
    isLoading: loadingSus,
    isError: errorSus,
  } = useGetSusByIdQuery(payload?.susId as number, { skip: !isEdit });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty, errors, submitCount },
  } = useForm<SusFormValues>({
    resolver: zodResolver(susFormSchema),
    defaultValues: {
      finess_geo: '',
      numero_ordre: undefined as unknown as number,
      nom: '',
      date_gel: null,
      adr_num_voie: '',
      adr_type_voie: '',
      adr_nom_voie: '',
      adr_lieudit: '',
      adr_code_postal: '',
      adr_commune: '',
      latitude: null,
      longitude: null,
      heure_premier_envoi_quotidien: '',
      reactif_j_1: true,
      transmission_autorisee: true,
      format_rpu: undefined as unknown as SusFormValues['format_rpu'],
      calcul_automatique_indicateurs: null,
      seuil_bas_quotidien: SEUIL_BAS_QUOTIDIEN_DEFAUT,
      seuil_haut_quotidien: SEUIL_HAUT_QUOTIDIEN_DEFAUT,
      seuil_bas_duree_passage: SEUIL_BAS_DUREE_PASSAGE_DEFAUT,
      seuil_haut_duree_passage: SEUIL_HAUT_DUREE_PASSAGE_DEFAUT,
      metadonnees: {
        logiciel: '',
        supra_etab: false,
        dpi_version: '',
        dpi_bascule: 'Non',
        dpi_bascule_precisions: '',
        transmission_rpu_clos: null,
      },
      contacts: [emptyContact],
    },
  });

  useEffect(() => {
    if (!isEdit || !sus) return;
    reset({
      finess_geo: sus.finess_geo,
      numero_ordre: Number(sus.numero_ordre),
      nom: sus.nom,
      date_gel: sus.date_gel,
      adr_num_voie: sus.adr_num_voie ?? '',
      adr_type_voie: sus.adr_type_voie ?? '',
      adr_nom_voie: sus.adr_nom_voie ?? '',
      adr_lieudit: sus.adr_lieudit ?? '',
      adr_code_postal: sus.adr_code_postal ?? '',
      adr_commune: sus.adr_commune ?? '',
      latitude: toNumberOrNull(sus.latitude),
      longitude: toNumberOrNull(sus.longitude),
      heure_premier_envoi_quotidien: toTimeInput(sus.heure_premier_envoi_quotidien),
      reactif_j_1: sus.reactif_j_1 ?? false,
      transmission_autorisee: sus.transmission_autorisee ?? false,
      format_rpu: sus.format_rpu as SusFormValues['format_rpu'],
      calcul_automatique_indicateurs: (sus.calcul_automatique_indicateurs ??
        null) as SusFormValues['calcul_automatique_indicateurs'],
      seuil_bas_quotidien: toNumberOrNull(sus.seuil_bas_quotidien) ?? SEUIL_BAS_QUOTIDIEN_DEFAUT,
      seuil_haut_quotidien: toNumberOrNull(sus.seuil_haut_quotidien) ?? SEUIL_HAUT_QUOTIDIEN_DEFAUT,
      seuil_bas_duree_passage:
        toNumberOrNull(sus.seuil_bas_duree_passage) ?? SEUIL_BAS_DUREE_PASSAGE_DEFAUT,
      seuil_haut_duree_passage:
        toNumberOrNull(sus.seuil_haut_duree_passage) ?? SEUIL_HAUT_DUREE_PASSAGE_DEFAUT,
      metadonnees: {
        logiciel: sus.metadonnees?.logiciel ?? '',
        supra_etab: sus.metadonnees?.supra_etab ?? false,
        dpi_version: sus.metadonnees?.dpi_version ?? '',
        dpi_bascule: sus.metadonnees?.dpi_bascule ?? null,
        dpi_bascule_precisions: sus.metadonnees?.dpi_bascule_precisions ?? '',
        transmission_rpu_clos: sus.metadonnees?.transmission_rpu_clos ?? null,
      },
      contacts: sus.contacts?.length
        ? sus.contacts.map(({ id, libelle, email }) => ({
            id,
            libelle: libelle ?? '',
            email: email ?? '',
          }))
        : [emptyContact],
    });
  }, [sus, isEdit, reset]);

  const numeroOrdreOptions = susNumeroOrdreCodes.map(code => ({
    value: String(code),
    label: t(`admin.sus.numero_ordre.${code}`),
  }));

  const onSubmit = async (data: SusFormValues) => {
    try {
      await saveSus({ ...toSaveArg(data), ...(isEdit ? { id: payload.susId } : {}) }).unwrap();
      dispatch(closeDrawer());
      notifications.show({
        color: 'green',
        message: isEdit ? t('admin.sus.form.success.update') : t('admin.sus.form.success.create'),
      });
    } catch {
      notifications.show({
        color: 'red',
        message: t('admin.sus.form.error.submit'),
      });
    }
  };

  if (isEdit && loadingSus) {
    return <SusFormDrawerLoader />;
  }

  if (isEdit && errorSus) {
    return (
      <Text c="red" role="alert">
        {t('admin.sus.form.error.load')}
      </Text>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack gap="md">
        {submitCount > 0 && Object.keys(errors).length > 0 && (
          <Alert color="red" role="alert">
            {t('admin.sus.form.error.summary')}
          </Alert>
        )}
        <Text size="xs" c="dimmed">
          {t('admin.sus.form.required_hint')}
        </Text>
        <Controller
          control={control}
          name="finess_geo"
          render={({ field, fieldState }) => (
            <EtablissementAutocomplete
              label={t('admin.sus.columns.finess_geo')}
              placeholder={t('profil.affectations.form.etablissement.placeholder')}
              value={field.value || null}
              onChange={val => field.onChange(val ?? '')}
              error={fieldState.error ? t('admin.sus.form.error.required') : undefined}
              disabled={isSubmitting || isEdit}
              required
            />
          )}
        />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
          <Controller
            control={control}
            name="numero_ordre"
            render={({ field, fieldState }) => (
              <Select
                ref={field.ref}
                name={field.name}
                onBlur={field.onBlur}
                value={field.value == null ? null : String(field.value)}
                onChange={value => field.onChange(value == null ? undefined : Number(value))}
                data={numeroOrdreOptions}
                label={t('admin.sus.columns.numero_ordre')}
                placeholder={t('admin.sus.form.numero_ordre.placeholder')}
                allowDeselect={false}
                error={fieldState.error ? t('admin.sus.form.error.required') : undefined}
                disabled={isSubmitting || isEdit}
                required
              />
            )}
          />
          <Controller
            control={control}
            name="date_gel"
            render={({ field, fieldState }) => (
              <DateInput
                {...field}
                label={t('admin.sus.columns.date_gel')}
                valueFormat="D MMMM YYYY"
                error={fieldState.error ? t('admin.sus.form.error.required') : undefined}
                disabled={isSubmitting}
                required
              />
            )}
          />
        </SimpleGrid>
        <Controller
          control={control}
          name="nom"
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label={t('admin.sus.columns.nom')}
              maxLength={50}
              error={fieldState.error ? t('admin.sus.form.error.required') : undefined}
              disabled={isSubmitting}
              required
            />
          )}
        />

        <Divider />
        <SusFormSection title={t('admin.sus.detail.address')}>
          <SusAddressFields control={control} disabled={isSubmitting} />
        </SusFormSection>
        <Divider />
        <SusFormSection
          title={t('admin.sus.form.contacts.title')}
          description={t('admin.sus.form.contacts.hint')}
        >
          <SusContactsFields control={control} disabled={isSubmitting} />
        </SusFormSection>
        <Divider />
        <SusFormSection title={t('admin.sus.form.section.transmission')}>
          <SusSettingsFields control={control} disabled={isSubmitting} />
        </SusFormSection>
        <Divider />
        <SusFormSection title={t('admin.sus.form.section.seuils')}>
          <SusThresholdFields control={control} disabled={isSubmitting} />
        </SusFormSection>
        <Divider />
        <SusFormSection title={t('admin.sus.form.section.dpi')}>
          <SusDpiFields control={control} disabled={isSubmitting} />
        </SusFormSection>
        <Divider />
        <SusFormSection title={t('admin.sus.detail.coordinates')}>
          <SusPositionField control={control} disabled={isSubmitting} />
        </SusFormSection>
        <Group
          justify="flex-end"
          py="sm"
          pos="sticky"
          bottom={0}
          bg="var(--mantine-color-body)"
          style={{ zIndex: 1, borderTop: '1px solid var(--mantine-color-default-border)' }}
        >
          <Button variant="default" onClick={() => dispatch(closeDrawer())} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={isSubmitting} disabled={!isDirty}>
            {isEdit ? t('admin.sus.form.update') : t('admin.sus.form.create')}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default SusFormDrawer;
